import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css";
import * as XLSX from "xlsx";

const RoomBookingReport = () => {
  const API_URL = process.env.REACT_APP_API_URL || "";
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchBookings = async () => {
    try {
      const response = await fetch(
        `${API_URL}list-bed-bookings.php?limit=10000`
      );
      const data = await response.json();
      if (Array.isArray(data.data)) {
        setBookings(data.data);
      } else {
        showToast("Failed to load bookings", "error");
      }
    } catch (error) {
      showToast("Network error fetching bookings", "error");
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line
  }, [API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.room_no && booking.room_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (booking.bed_no && booking.bed_no.toLowerCase().includes(searchTerm.toLowerCase()));
    const bookingDate = booking.checking_date;
    const afterStart = !startDate || bookingDate >= startDate;
    const beforeEnd = !endDate || bookingDate <= endDate;
    return matchesSearch && afterStart && beforeEnd;
  });

  const totalAmount = filteredBookings.reduce((sum, booking) => sum + Number(booking.total_amount || 0), 0);

  const openModal = (imageName) => {
    setModalImage(`${API_URL}uploads/${imageName}`);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage("");
  };

  const handleView = (bookingId) => {
    const found = bookings.find(b => b.id === bookingId);
    setBookingDetails(found || null);
    setDetailsModalOpen(true);
  };

  const handleExport = () => {
    if (filteredBookings.length === 0) return;
    const exportData = filteredBookings.map((b) => ({
      "Customer Name": b.customer_name,
      "Address": b.address,
      "Check-in Date": b.checking_date,
      "Check-out Date": b.checkout_date,
      "Room No": b.room_no,
      "Bed No": b.bed_no,
      "No. of Days": b.nodays,
      "Per Day Price": b.per_day_price,
      "Total Amount": b.total_amount,
      "ID Proof Type": b.id_proof_type,
    }));
    // Add total row
    const totalAmount = exportData.reduce((sum, row) => sum + Number(row["Total Amount"] || 0), 0);
    exportData.push({
      "Customer Name": "",
      "Address": "",
      "Check-in Date": "",
      "Check-out Date": "",
      "Room No": "",
      "Bed No": "",
      "No. of Days": "",
      "Per Day Price": "Total Amount",
      "Total Amount": totalAmount,
      "ID Proof Type": ""
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, `RoomBookingReport_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const formatDateTime12Hour = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr.replace(/-/g, '/'));
    if (isNaN(date.getTime())) return dateTimeStr;
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString(undefined, options);
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Room Booking Report</h1>
          <p>View all customer bookings (report view)</p>
        </div>
      </div>

      <div className="filters-section" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <label htmlFor="start-date" style={{ fontWeight: 500, marginRight: 4, whiteSpace: "nowrap" }}>Check-in Date:</label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", marginRight: 12 }}
            placeholder="Check-in Date"
          />
          <label htmlFor="end-date" style={{ fontWeight: 500, marginRight: 4, whiteSpace: "nowrap" }}>Check-out Date:</label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", marginRight: 12 }}
            placeholder="Check-out Date"
          />
          <input
            type="text"
            placeholder="Search by customer, room, or bed..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", minWidth: 220 }}
          />
        </div>
        <button
          onClick={handleExport}
          style={{ padding: "0.6rem 1.5rem", background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "1rem", cursor: "pointer" }}
        >
          Export
        </button>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Customer Details</th>
              <th>Room / Bed</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Total Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((booking) => (
              <tr key={booking.id}>
                <td>
                  <div className="unit-info">
                    <div
                      className="unit-icon"
                      style={{
                        backgroundImage: `url(${API_URL}uploads/${booking.id_proof_image})`,
                        backgroundSize: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() => openModal(booking.id_proof_image)}
                    ></div>
                    <div>
                      <strong>{booking.customer_name}</strong>
                      <p>{booking.address}</p>
                    </div>
                  </div>
                </td>
                <td>
                  {booking.room_no || "N/A"} / {booking.bed_no || "N/A"}
                </td>
                <td>{new Date(booking.checking_date).toLocaleDateString()}</td>
                <td>{new Date(booking.checkout_date).toLocaleDateString()}</td>
                <td>₹{booking.total_amount}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn view"
                      onClick={() => handleView(booking.id)}
                    >
                      View
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredBookings.length > 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: "bold", background: "#f9f871" }}>
                  Total Amount
                </td>
                <td style={{ fontWeight: "bold", background: "#f9f871", color: "#d32f2f" }}>
                  ₹{totalAmount}
                </td>
                <td style={{ background: "#f9f871" }}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              {" "}
              ×{" "}
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "none",
              color: "white",
              border: "none",
              fontSize: "3em",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
          <img
            src={modalImage}
            alt="ID Proof"
            style={{
              maxWidth: "85%",
              maxHeight: "85%",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {detailsModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1001,
          }}
          onClick={() => setDetailsModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "2em",
              borderRadius: "12px",
              minWidth: "650px",
              maxWidth: "95vw",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              fontFamily: "Segoe UI, Arial, sans-serif"
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setDetailsModalOpen(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "20px",
                background: "none",
                color: "#333",
                border: "none",
                fontSize: "2em",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
            {bookingDetails ? (
              <div>
                <h2 style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  marginBottom: "1.5rem",
                  letterSpacing: "0.5px",
                  color: "#222"
                }}>Booking Details</h2>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                  <tbody>
                    {[
                      ["Customer Name", bookingDetails.customer_name],
                      ["Address", bookingDetails.address],
                      ["ID Proof Type", bookingDetails.id_proof_type],
                      ["ID Proof Image", (
                        <img
                          src={`${API_URL}uploads/${bookingDetails.id_proof_image}`}
                          alt="ID Proof"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "120px",
                            borderRadius: '6px',
                            border: '1px solid #eee',
                            display: "block",
                            margin: "0.5em auto"
                          }}
                        />
                      )],
                      ["Room No", bookingDetails.room_no],
                      ["Bed No", bookingDetails.bed_no],
                      ["Check-in", bookingDetails.checking_date],
                      ["Check-out", bookingDetails.checkout_date],
                      ["No. of Days", bookingDetails.nodays],
                      ["Per Day Price", `₹${bookingDetails.per_day_price}`],
                      ["Total Amount", `₹${bookingDetails.total_amount}`],
                      ["Customer Details", bookingDetails.customer_details],
                      ["Customer E-Pass", (
                        <img
                          src={`${API_URL}uploads/${bookingDetails.customer_getepass}`}
                          alt="E-Pass"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "120px",
                            borderRadius: '6px',
                            border: '1px solid #eee',
                            display: "block",
                            margin: "0.5em auto"
                          }}
                        />
                      )],
                      ["Created At", formatDateTime12Hour(bookingDetails.created_at)]
                    ].map(([label, value], idx) => (
                      <tr key={label} style={{
                        background: idx % 2 === 0 ? "#fafbfc" : "#fff"
                      }}>
                        <td style={{
                          fontWeight: 600,
                          padding: '12px 16px',
                          border: 'none',
                          color: "#333",
                          width: "200px",
                          background: "#f4f6f8"
                        }}>{label}</td>
                        <td style={{
                          padding: '12px 16px',
                          border: 'none',
                          color: "#222"
                        }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>No details found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBookingReport; 